import { useState, useEffect, useCallback, useRef } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutter
const VERSION_URL = '/version.json';

interface VersionInfo {
  hash: string;
  buildTime: string;
}

export function useVersionCheck() {
  const [newVersionAvailable, setNewVersionAvailable] = useState(false);
  // Klientens egen build-hash bakt inn i index.html ved build (generate-version.js).
  // Ved å lese dette ved mount — fremfor å lagre første server-respons — oppdager
  // vi korrekt at klienten kjører en utdatert bundle, selv om nettleseren åpnet
  // siden etter at ny versjon ble deployet.
  const initialHash = useRef<string | null>(
    typeof window !== 'undefined' ? (window as any).__BUILD_HASH ?? null : null
  );

  const checkVersion = useCallback(async () => {
    try {
      // Cache-bust for å alltid hente siste versjon fra server
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`);
      if (!response.ok) return;

      const data: VersionInfo = await response.json();
      if (!data.hash || data.hash === 'dev') return;

      if (initialHash.current === null) {
        // Fallback hvis __BUILD_HASH ikke er bakt inn (f.eks. lokal dev uten
        // generate-version.js): bruk første server-respons som baseline.
        initialHash.current = data.hash;
      } else if (data.hash !== initialHash.current) {
        setNewVersionAvailable(true);
      }
    } catch {
      // Ignorer nettverksfeil — prøver igjen ved neste poll
    }
  }, []);

  const refresh = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    checkVersion();

    const interval = setInterval(checkVersion, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [checkVersion]);

  return { newVersionAvailable, refresh };
}
